import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../services/api';
import Button from '../../components/common/Button/Button';
import SearchSelect from '../../components/common/SearchSelect/SearchSelect';
import styles from './AddProPlanTransaction.module.css';

const planTypeOptions = [
    { label: 'Monthly', value: 1 },
    { label: 'Quarterly', value: 2 },
    { label: 'Half Yearly', value: 3 },
    { label: 'Yearly', value: 4 },
];

const formatDateTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const calculateDeactivationDate = (activationDateStr, validityStr) => {
    if (!activationDateStr || !validityStr) return '';
    const d = new Date(activationDateStr);
    if (isNaN(d.getTime())) return '';
    
    const v = validityStr.toLowerCase();
    if (v.includes('day') && !v.includes('days')) d.setDate(d.getDate() + 1);
    else if (v.includes('days')) {
        const num = parseInt(v) || 1;
        d.setDate(d.getDate() + num);
    }
    else if (v.includes('week')) d.setDate(d.getDate() + 7);
    else if (v.includes('month')) {
        const num = parseInt(v) || 1;
        d.setMonth(d.getMonth() + num);
    }
    else if (v.includes('year')) {
        const num = parseInt(v) || 1;
        d.setFullYear(d.getFullYear() + num);
    }
    
    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AddProPlanTransaction = ({ transactionId, onSubmitted, onCancel }) => {
    const id = transactionId;
    const isEditing = Boolean(id);
    const [loading, setLoading] = useState(false);
    const [fetchingDetail, setFetchingDetail] = useState(isEditing);
    const [resultData, setResultData] = useState(null);

    // Users State
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userSearchError, setUserSearchError] = useState("");

    // Plans State
    const [plans, setPlans] = useState([]);

    const [userDrafts, setUserDrafts] = useState([]);
    const [isManualTxId, setIsManualTxId] = useState(false);

    const [formData, setFormData] = useState({
        user_id: '',
        user_name: '',
        transaction_id: '',
        internal_transaction_id: '',
        plan_id: '',
        plan_type_id: '',
        plan_activation_type_id: 2,
        plan_activation_date: formatDateTime(new Date()),
        plan_deactivation_date: '',
        plan_status: 'Active',
        plan_status_note: '',
        payment_gateway: ''
    });

    const [channels, setChannels] = useState({ push: true, email: true });

    useEffect(() => {
        api.get('/plans-new').then(res => {
            const planList = res?.result || res?.data?.result || [];
            if (Array.isArray(planList)) {
                setPlans(planList.map(p => ({
                    value: p.id,
                    label: p.name,
                    validity: p.validity
                })));
            }
        }).catch(err => console.error("Failed to fetch plans", err));
    }, []);

    useEffect(() => {
        if (!isEditing) return;

        api.get(`/proplantransaction`)
            .then(response => {
                let dataArray = response?.result || response?.data || response || [];
                if (typeof dataArray === 'string') {
                    try { dataArray = JSON.parse(dataArray); } catch (e) { }
                }
                const list = Array.isArray(dataArray) ? dataArray : [];
                const data = list.find(t => String(t.pro_plans_transaction_id) === String(id));

                if (!data) {
                    toast.error("Transaction not found in database");
                    if (onCancel) onCancel();
                    return;
                }

                setFormData({
                    user_id: data.user_id || '',
                    user_name: data.user_name || '',
                    transaction_id: data.transaction_id || '',
                    internal_transaction_id: data.internal_transaction_id || '',
                    plan_id: data.plan_id || '',
                    plan_type_id: data.plan_type_id || '',
                    plan_activation_type_id: data.plan_activation_type_id || 2,
                    plan_activation_date: formatDateTime(data.plan_activation_date),
                    plan_deactivation_date: formatDateTime(data.plan_deactivation_date),
                    plan_status: data.plan_status || 'Active',
                    plan_status_note: data.plan_status_note || '',
                    payment_gateway: data.payment_method || data.payment_gateway || ''
                });

                if (data.user_id) {
                    setUsers([{
                        value: data.user_id,
                        label: `${data.user_name || 'User'} - ID: ${data.user_id}`,
                        name: data.user_name || 'User'
                    }]);
                }
            })
            .catch(err => {
                console.error("Failed to load transaction details", err);
                toast.error("Failed to load details. They might not exist.");
            })
            .finally(() => {
                setFetchingDetail(false);
            });
    }, [id, isEditing]);

    const fetchUsers = async (searchTerm) => {
        setUserSearchError("");
        if (!searchTerm || !searchTerm.trim()) {
            setUsers([]);
            return;
        }
        try {
            setLoadingUsers(true);
            const url = `/users?search=${encodeURIComponent(searchTerm)}&limit=20`;
            const res = await api.get(url);
            const userList = res?.data?.data || res?.data || [];
            if (Array.isArray(userList)) {
                if (userList.length === 0 && /^\d{10}$/.test(searchTerm.trim())) {
                    setUserSearchError("No user found");
                }
                setUsers(userList.map(u => ({
                    value: u.userid,
                    label: `${u.name || u.username || 'User'} - ID: ${u.userid}`,
                    name: u.name || u.username || 'User'
                })));
            }
        } catch (err) {
            console.error("Failed to fetch users", err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchDraftInvoices = async (userId, userName) => {
        if (!userId) return;
        try {
            const res = await api.get(`/invoice/draft/user/${userId}`);
            let drafts = res?.result || res?.data?.result || [];
            if (!Array.isArray(drafts)) drafts = [];
            setUserDrafts(drafts);
            if (drafts.length > 0) {
                toast.success(`Found ${drafts.length} draft invoice(s). Select one from Internal DB Transaction ID.`);
            }
        } catch (error) {
            console.log("No draft invoices found for this user");
            setUserDrafts([]);
        }
    };

    const handleDraftSelect = (e) => {
        const val = e.target.value;
        if (val === "manual") {
            setIsManualTxId(true);
            setFormData(prev => ({ ...prev, internal_transaction_id: '' }));
            return;
        }

        setIsManualTxId(false);
        const draft = userDrafts.find(d => String(d.internal_transaction_id) === String(val));
        if (!draft) {
            handleChange(e);
            return;
        }

        setFormData(prev => {
            let newTypeId = prev.plan_type_id;
            let newDeactivation = prev.plan_deactivation_date;
            let matchedPlanId = prev.plan_id;
            
            // Match plan by name
            if (draft.plan_name) {
                const selectedPlan = plans.find(p => p.label && p.label.toLowerCase().trim() === draft.plan_name.toLowerCase().trim());
                if (selectedPlan) {
                    matchedPlanId = selectedPlan.value;
                }
            }

            const v = (draft.plan_validity || (matchedPlanId && plans.find(p => p.value === matchedPlanId)?.validity) || "").toLowerCase().trim();
            if (v.includes("month") && !v.includes("3") && !v.includes("6") && !v.includes("12")) newTypeId = 1;
            else if (v.includes("quarter") || v.includes("3 month") || v === "3 months") newTypeId = 2;
            else if (v.includes("half") || v.includes("6 month") || v === "6 months") newTypeId = 3;
            else if (v.includes("year") || v.includes("12 month") || v === "12 months") newTypeId = 4;
            
            if (v) {
                newDeactivation = calculateDeactivationDate(prev.plan_activation_date, v);
            }

            return {
                ...prev,
                internal_transaction_id: draft.internal_transaction_id || prev.internal_transaction_id,
                transaction_id: draft.service_provider_tid || draft.transaction_id || prev.transaction_id,
                plan_id: matchedPlanId,
                plan_type_id: newTypeId,
                plan_deactivation_date: newDeactivation
            };
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePlanSelect = (e) => {
        const selectedId = e.target.value;
        const selectedPlan = plans.find(p => String(p.value) === String(selectedId));
        
        setFormData(prev => {
            let newTypeId = prev.plan_type_id;
            let newDeactivation = prev.plan_deactivation_date;
            
            if (selectedPlan) {
                const v = selectedPlan.validity ? selectedPlan.validity.toLowerCase() : "";
                if (v.includes("month") && !v.includes("3") && !v.includes("6")) newTypeId = 1;
                else if (v.includes("quarter") || v.includes("3 month")) newTypeId = 2;
                else if (v.includes("half") || v.includes("6 month")) newTypeId = 3;
                else if (v.includes("year")) newTypeId = 4;
                
                newDeactivation = calculateDeactivationDate(prev.plan_activation_date, selectedPlan.validity);
            }

            return {
                ...prev,
                plan_id: selectedId,
                plan_type_id: newTypeId,
                plan_deactivation_date: newDeactivation
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.user_id || !formData.plan_id || !formData.plan_activation_date || !formData.plan_type_id) {
            toast.error("User ID, Plan, Plan Type, and Activation Date are required.");
            return;
        }

        setLoading(true);
        try {
            const channelsArray = [];
            if (channels.push) channelsArray.push("push");
            if (channels.email) channelsArray.push("email");

            const payload = {
                ...formData,
                user_id: Number(formData.user_id),
                plan_id: Number(formData.plan_id),
                plan_type_id: Number(formData.plan_type_id),
                plan_activation_type_id: Number(formData.plan_activation_type_id),
                channels: JSON.stringify(channelsArray),
            };

            let res;
            if (isEditing) {
                res = await api.put(`/proplantransaction/${id}`, payload);
            } else {
                res = await api.post('/proplantransaction', payload);
            }

            const notifs = res.data?.notifications || res.notifications;
            const emailStatus = notifs?.email;
            const pushStatus = notifs?.push;
            
            let statusMessage = "Transaction saved.";
            if (emailStatus?.success && pushStatus?.success) {
                statusMessage += " Notification & email has been sent.";
            } else if (emailStatus?.success) {
                statusMessage += " Email has been sent.";
            } else if (pushStatus?.success) {
                statusMessage += " Push notification has been sent.";
            }

            toast.success(statusMessage);

            setResultData({
                transaction_id: formData.internal_transaction_id || formData.transaction_id || "N/A",
                notifications: notifs,
                isEditing: isEditing,
                statusMessage: statusMessage
            });
        } catch (error) {
            console.error("Error saving transaction:", error);
            const errMsg = error.response?.data?.message || "Failed to save transaction";
            toast.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    if (resultData) {
        return (
            <div className={styles.container}>
                <div className={styles.resultCard}>
                    <div className={styles.resultTitle}>Response Result</div>
                    <div className={styles.resultList}>
                        <div className={styles.resultItem}>
                            <span className={styles.resultSuccess}>✓</span> Transaction {resultData.isEditing ? "updated" : "saved"} successfully.
                        </div>
                        
                        {resultData.notifications?.push && (
                            <div className={styles.resultItem}>
                                {resultData.notifications.push.success ? (
                                    <span className={styles.resultSuccess}>✓</span>
                                ) : (
                                    <span className={styles.resultError}>✗</span>
                                )} 
                                Push notification {resultData.notifications.push.success ? 'sent successfully.' : `failed: ${resultData.notifications.push.message || 'Error'}`}
                            </div>
                        )}

                        {resultData.notifications?.email && (
                            <div className={styles.resultItem}>
                                {resultData.notifications.email.success ? (
                                    <span className={styles.resultSuccess}>✓</span>
                                ) : (
                                    <span className={styles.resultError}>✗</span>
                                )} 
                                Email notification {resultData.notifications.email.success ? 'sent successfully.' : `failed: ${resultData.notifications.email.message || 'Error'}`}
                            </div>
                        )}
                    </div>
                    <button 
                        type="button" 
                        className={`${styles.submitBtn || ''} ${styles.resultCloseBtn}`}
                        onClick={() => {
                            if (onSubmitted) onSubmitted(); else if (onCancel) onCancel();
                        }}
                        style={{ background: "var(--color-primary, #0f172a)", color: "white", padding: "10px", border: "none", borderRadius: "5px", cursor: "pointer", fontWeight: "bold" }}
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.card}>

                {fetchingDetail ? (
                    <div className={styles.loadingState}>Loading transaction details...</div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className={styles.formGrid}>
                            
                            <div className={styles.formGroup}>
                                <SearchSelect
                                    label="Select User"
                                    name="user_id"
                                    placeholder="Search User..."
                                    options={users}
                                    value={formData.user_id}
                                    onSearch={fetchUsers}
                                    error={userSearchError}
                                    isLoading={loadingUsers}
                                    onChange={(e) => {
                                        const selectedUser = users.find(u => String(u.value) === String(e.target.value));
                                        handleChange({ target: { name: 'user_id', value: e.target.value } });
                                        setUserSearchError("");
                                        if (selectedUser) {
                                            handleChange({ target: { name: 'user_name', value: selectedUser.name } });
                                            fetchDraftInvoices(selectedUser.value, selectedUser.name);
                                        }
                                    }}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>User Name</label>
                                <input
                                    type="text"
                                    name="user_name"
                                    value={formData.user_name}
                                    onChange={handleChange}
                                    placeholder="e.g. Aman Gupta"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Payment Gateway</label>
                                <select
                                    name="payment_gateway"
                                    value={formData.payment_gateway}
                                    onChange={handleChange}
                                    className={styles.input}
                                >
                                    <option value="">Select Gateway</option>
                                    <option value="upi">UPI</option>
                                    <option value="razorpay">RazorPay</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Gateway Transaction ID</label>
                                <input
                                    type="text"
                                    name="transaction_id"
                                    value={formData.transaction_id}
                                    onChange={handleChange}
                                    placeholder={formData.payment_gateway === 'upi' ? 'e.g. 426831955540...' : 'e.g. pay_STlc9jq...'}
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Internal DB Transaction ID</label>
                                {userDrafts.length > 0 && !isManualTxId ? (
                                    <select
                                        name="internal_transaction_id"
                                        value={formData.internal_transaction_id}
                                        onChange={handleDraftSelect}
                                        className={styles.input}
                                    >
                                        <option value="">Select a Draft Invoice</option>
                                        {userDrafts.map(d => (
                                            <option key={d.internal_transaction_id} value={d.internal_transaction_id}>
                                                TX: {d.internal_transaction_id} - {d.plan_name}
                                            </option>
                                        ))}
                                        <option value="manual">Enter Manually...</option>
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        name="internal_transaction_id"
                                        value={formData.internal_transaction_id}
                                        onChange={handleChange}
                                        placeholder="e.g. 158"
                                        className={styles.input}
                                    />
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Status <span className={styles.required}>*</span></label>
                                <select
                                    name="plan_status"
                                    value={formData.plan_status}
                                    onChange={handleChange}
                                    className={styles.input}
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                    <option value="Expired">Expired</option>
                                    <option value="Cancelled">Cancelled</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Plan <span className={styles.required}>*</span></label>
                                <select
                                    name="plan_id"
                                    value={formData.plan_id}
                                    onChange={handlePlanSelect}
                                    className={styles.input}
                                    required
                                >
                                    <option value="">Select a Plan</option>
                                    {plans.map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Plan Type <span className={styles.required}>*</span></label>
                                <select
                                    name="plan_type_id"
                                    value={formData.plan_type_id}
                                    onChange={handleChange}
                                    className={styles.input}
                                    required
                                >
                                    <option value="">Select Plan Type</option>
                                    {planTypeOptions.map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Plan Activation Type <span className={styles.required}>*</span></label>
                                <input
                                    type="text"
                                    value="Manual Invoice"
                                    className={styles.input}
                                    disabled
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Activation Date <span className={styles.required}>*</span></label>
                                <input
                                    type="datetime-local"
                                    name="plan_activation_date"
                                    value={formData.plan_activation_date}
                                    onChange={(e) => {
                                        handleChange(e);
                                        const selectedPlan = plans.find(p => String(p.value) === String(formData.plan_id));
                                        if (selectedPlan) {
                                            setFormData(prev => ({
                                                ...prev,
                                                plan_deactivation_date: calculateDeactivationDate(e.target.value, selectedPlan.validity)
                                            }));
                                        }
                                    }}
                                    className={styles.input}
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.label}>Deactivation Date</label>
                                <input
                                    type="datetime-local"
                                    name="plan_deactivation_date"
                                    value={formData.plan_deactivation_date}
                                    onChange={handleChange}
                                    className={styles.input}
                                />
                            </div>

                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <label className={styles.label}>Status Note (Optional)</label>
                                <textarea
                                    name="plan_status_note"
                                    value={formData.plan_status_note}
                                    onChange={handleChange}
                                    placeholder="Provide internal reasoning or notes..."
                                    className={styles.input}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: "20px", marginBottom: "20px", display: "flex", gap: "20px", alignItems: "center", padding: "0 5px" }}>
                            <strong style={{ fontSize: "14px" }}>Notify User:</strong>
                            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
                            <input 
                                type="checkbox" 
                                checked={channels.push}
                                onChange={(e) => setChannels(prev => ({ ...prev, push: e.target.checked }))}
                                style={{ width: "16px", height: "16px", cursor: "pointer" }}
                            />
                            Push Notification
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
                            <input 
                                type="checkbox" 
                                checked={channels.email}
                                onChange={(e) => setChannels(prev => ({ ...prev, email: e.target.checked }))}
                                style={{ width: "16px", height: "16px", cursor: "pointer" }}
                            />
                            Email Notification
                            </label>
                        </div>

                        <div className={styles.actions}>
                            <Button
                                variant="secondary"
                                onClick={onCancel}
                                type="button"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? "Saving..." : (isEditing ? "Update Transaction" : "Save Transaction")}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddProPlanTransaction;
